import { z } from 'zod';

/*
 * immoOS as the OAuth 2.1 authorization server for the workspace MCP surface
 * (IMM-601.8.3).
 *
 * Every schema in this file describes a WIRE FORMAT somebody else specified —
 * RFC 8414 (authorization-server metadata), RFC 7591 (dynamic client
 * registration), RFC 6749 §4.1/§5 (authorize + token) and RFC 7009
 * (revocation). Two consequences, and both are deliberate:
 *
 *  1. **Field names are snake_case**, against the camelCase every other schema
 *     in this package uses. They are not ours to rename — a conforming client
 *     reads `client_id`, not `clientId`.
 *  2. **Requests are permissive and responses are strict.** Nothing here
 *     validates a request into a `400`: an OAuth error is an RFC-shaped body or
 *     a redirect query parameter, decided by the handler after it knows whether
 *     it holds a redirect target it may safely use. A zod refusal would render
 *     the immoOS error envelope instead — as a JSON body in a browser tab on
 *     `/v1/oauth/authorize`, and as a shape no client parses on
 *     `/v1/oauth/token`.
 */

/**
 * RFC 6749 §5.2 / §4.1.2.1. The whole error surface of this server, as a client
 * reads it: an identifier from the RFC's own registry plus optional prose.
 *
 * Deliberately NOT an immoOS error code and deliberately not translated — these
 * are read by a program, never by a person. The one refusal a MEMBER reads
 * (`identity.oauth-consent.invalid-request`) rides the ordinary envelope with a
 * German leaf, and the authorize endpoint is the single leaf that has to choose
 * between the two.
 */
export const OauthProtocolErrorSchema = z.object({
  error: z.string(),
  error_description: z.string().optional(),
});

export type OauthProtocolError = z.infer<typeof OauthProtocolErrorSchema>;

/**
 * RFC 8414 authorization-server metadata.
 *
 * `issuer` MUST be byte-identical to the identifier the well-known URL was
 * constructed from, which is why it has no path component: a conforming client's
 * very first discovery attempt (`<issuer>/.well-known/oauth-authorization-server`)
 * then succeeds with no path-insertion fallback and no OIDC probing.
 *
 * `registration_endpoint` is present and `client_id_metadata_document_supported`
 * is deliberately ABSENT: a client without a credential self-registers (RFC
 * 7591) rather than pointing us at a URL to fetch. CIMD is the one registration
 * mechanism that would make this backend issue an attacker-influenced outbound
 * request, and it stays declined.
 *
 * `authorization_response_iss_parameter_supported` is a MUST once the server
 * emits `iss` on authorization responses, which it does — on success AND on
 * `access_denied`, because a mix-up defence that covers only the happy path
 * defends the half nobody attacks.
 */
export const AuthorizationServerMetadataSchema = z.object({
  issuer: z.url(),
  authorization_endpoint: z.url(),
  token_endpoint: z.url(),
  revocation_endpoint: z.url(),
  registration_endpoint: z.url(),
  scopes_supported: z.array(z.string()),
  response_types_supported: z.array(z.literal('code')),
  grant_types_supported: z.array(z.enum(['authorization_code', 'refresh_token'])),
  code_challenge_methods_supported: z.array(z.literal('S256')),
  token_endpoint_auth_methods_supported: z.array(z.literal('none')),
  authorization_response_iss_parameter_supported: z.literal(true),
});

export type AuthorizationServerMetadata = z.infer<typeof AuthorizationServerMetadataSchema>;

/**
 * RFC 7591 §2 client metadata, as a client sends it.
 *
 * `looseObject` because the RFC's metadata registry is open-ended and a client
 * that sends `logo_uri` or `contacts` must not be refused for it — we read the
 * five fields we act on and ignore the rest. Every field is optional at the
 * SCHEMA level so the handler renders RFC 7591 §3.2.2's `invalid_client_metadata`
 * / `invalid_redirect_uri` rather than a zod envelope.
 *
 * `scope` is accepted and IGNORED: a self-registered client cannot set its own
 * ceiling, and the ceiling that actually binds is the consenting member's.
 */
export const RegisterOauthClientBodySchema = z.looseObject({
  redirect_uris: z.array(z.string()).optional(),
  client_name: z.string().optional(),
  grant_types: z.array(z.string()).optional(),
  response_types: z.array(z.string()).optional(),
  token_endpoint_auth_method: z.string().optional(),
  scope: z.string().optional(),
});

export type RegisterOauthClientBody = z.infer<typeof RegisterOauthClientBodySchema>;

/**
 * RFC 7591 §3.2.1. A PUBLIC client always: no `client_secret` is issued, stored
 * or accepted anywhere on this surface, so there is nothing here for a client to
 * lose.
 */
export const RegisterOauthClientResponseSchema = z.object({
  client_id: z.string(),
  /** Seconds since the epoch, per the RFC — not an ISO timestamp. */
  client_id_issued_at: z.number().int(),
  redirect_uris: z.array(z.string()),
  client_name: z.string(),
  token_endpoint_auth_method: z.literal('none'),
  grant_types: z.array(z.string()),
  response_types: z.array(z.string()),
});

export type RegisterOauthClientResponse = z.infer<typeof RegisterOauthClientResponseSchema>;

/**
 * The authorize query. EVERY field is optional and untyped beyond `string`, and
 * that is the security-relevant part: this leaf is a browser NAVIGATION, so a
 * zod refusal would paint `{"error":{"code":…}}` across a member's tab. The
 * handler validates instead, and picks its rendering by whether it has a
 * validated redirect target — no target means the member is the only reader.
 */
export const AuthorizeOauthQuerySchema = z.looseObject({
  client_id: z.string().optional(),
  redirect_uri: z.string().optional(),
  response_type: z.string().optional(),
  /** Space-delimited per RFC 6749 §3.3. Split by the handler, never here. */
  scope: z.string().optional(),
  state: z.string().optional(),
  code_challenge: z.string().optional(),
  code_challenge_method: z.string().optional(),
  /** RFC 8707. Required by this server; absence is `invalid_target`, not a 400. */
  resource: z.string().optional(),
});

/**
 * RFC 6749 §4.1.3 / §6, `application/x-www-form-urlencoded`. Permissive for the
 * same reason as the authorize query: a malformed exchange is `invalid_request`
 * in the RFC's own JSON, never the immoOS envelope.
 */
export const OauthTokenBodySchema = z.looseObject({
  grant_type: z.string().optional(),
  code: z.string().optional(),
  code_verifier: z.string().optional(),
  redirect_uri: z.string().optional(),
  client_id: z.string().optional(),
  resource: z.string().optional(),
  refresh_token: z.string().optional(),
});

/**
 * RFC 6749 §5.1. `scope` names what was actually GRANTED — never what the client
 * asked for — because the grant may have been narrowed at consent and narrowed
 * again by a later demotion, and a client that believes it holds more than it
 * does fails in ways its author cannot debug.
 */
export const OauthTokenResponseSchema = z.object({
  access_token: z.string(),
  token_type: z.literal('Bearer'),
  expires_in: z.number().int(),
  refresh_token: z.string(),
  /** Space-delimited, per the RFC. Empty when the consent granted nothing. */
  scope: z.string(),
});

export type OauthTokenResponse = z.infer<typeof OauthTokenResponseSchema>;

/**
 * RFC 7009 §2.1. `token_type_hint` is accepted and ignored — we look the digest
 * up in one table that holds both kinds, so the hint saves nothing.
 *
 * There is no `client_id` field to validate. Every client here is public, so
 * RFC 7009's client-authentication requirement does not apply, and checking an
 * unauthenticated caller-supplied identifier would prove nothing while turning
 * the endpoint into the existence oracle the uniform `200` exists to prevent.
 */
export const RevokeOauthTokenBodySchema = z.looseObject({
  token: z.string().optional(),
  token_type_hint: z.string().optional(),
});
